/** @jsxImportSource @emotion/react */
import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { CellType } from '@grapecity/wijmo.grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Button } from '@mui/material';
import { Generic } from 'models/ip/Generic';
import { CrudCode } from 'models/common/Edit';
import { CommonYN } from 'models/common/Common';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { tb } from 'components/layouts/Table';
import PartnerUserModal from 'components/modals/common/PartnerUserModal';
import { Partner, PartnerUser } from 'models/common/Partner';
import PartnerModal from 'components/modals/common/PartnerModal';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { Employee } from 'models/admin/Employee';
import UserModal from 'components/modals/common/UserModal';
import TableCell from '@mui/material/TableCell';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';

interface paramData {
  condition: any;
  editable: boolean;
  callBackRowData?: any;
}

const TemplateValue = {
  maker: '',
  makerCd: '',
  makerInspector: '',
  makerInspectorNm: '',
  fatEnsolInspector: '',
  fatEnsolInspectorNm: '',
  satEnsolInspector: '',
  satEnsolInspectorNm: '',
};

const FacilityTargetGrid = forwardRef((props: paramData, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<Generic[]>([]);
  const [templateValue, setTemplateValue] = useState(TemplateValue);

  const [isOpenPartnerUserModal, setOpenPartnerUserModal] = useState<boolean>(false);
  const [partnerUserModalCondition, setPartnerUserModalCondition] = useState<any>({});

  const [isOpenPartnerModal, setOpenPartnerModal] = useState<boolean>(false);
  const [partnerModalCondition, setPartnerModalCondition] = useState<any>({});

  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [userModalCondition, setUserModalCondition] = useState<any>({});

  const btnAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
      equipmentId: '',
      equipmentName: '',
      maker: templateValue.maker,
      makerCd: templateValue.makerCd,
      fatMakerInspector: templateValue.makerInspector,
      fatMakerInspectorNm: templateValue.makerInspectorNm,
      fatEnsolInspector: templateValue.fatEnsolInspector,
      fatEnsolInspectorNm: templateValue.fatEnsolInspectorNm,
      satMakerInspector: templateValue.makerInspector,
      satMakerInspectorNm: templateValue.makerInspectorNm,
      satEnsolInspector: templateValue.satEnsolInspector,
      satEnsolInspectorNm: templateValue.satEnsolInspectorNm,
    } as Generic;
    setRowData([newRow, ...rowData]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (selectedRows.length < 1) return;

    selectedRows.forEach((row) => {
      const item = row.dataItem;
      if (item.crudKey === CrudCode.CREATE) {
        gridRef.current.collectionView.remove(item);
      } else {
        item.crudKey = CrudCode.DELETE;
      }
    });
    gridRef.current.refresh();
  };

  const layoutDefinition = [
    {
      binding: 'equipmentId',
      header: String(t('ip.grid.Main설비ID', 'Main설비ID')),
      align: 'left',
      width: 240,
    },
    {
      binding: 'equipmentName',
      header: String(t('com.label.설비명', '설비명')),
      align: 'left',
      width: 150,
    },
    {
      binding: 'maker',
      header: String(t('ip.grid.Maker', 'Maker')),
      width: 150,
      align: 'left',
      cssClass: 'WijmoFind',
      isReadOnly: true,
      isRequired: false,
      cellTemplate: (params) => `
        <span>${params.value || ''}</span>
        <Button /> 
      `,
    },
    {
      header: String(t('ip.label.FAT', 'FAT')),
      align: 'center',
      columns: [
        {
          binding: 'fatMakerInspectorNm',
          header: String(t('ip.label.Maker 담당자', 'Maker 담당자')),
          width: 150,
          align: 'left',
          cssClass: 'WijmoFind',
          isReadOnly: true,
          isRequired: false,
          cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
        },
        {
          binding: 'fatEnsolInspectorNm',
          header: String(t('com.label.엔솔 담당자', '엔솔 담당자')),
          width: 150,
          align: 'left',
          cssClass: 'WijmoFind',
          isReadOnly: true,
          isRequired: false,
          cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
        },
      ],
    },
    {
      header: String(t('ip.label.SAT', 'SAT')),
      columns: [
        {
          binding: 'satMakerInspectorNm',
          header: String(t('ip.label.Maker 담당자', 'Maker 담당자')),
          width: 150,
          align: 'left',
          cssClass: 'WijmoFind',
          isReadOnly: true,
          isRequired: false,
          cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
        },
        {
          binding: 'satEnsolInspectorNm',
          header: String(t('com.label.엔솔 담당자', '엔솔 담당자')),
          width: 150,
          align: 'left',
          cssClass: 'WijmoFind',
          isReadOnly: true,
          isRequired: false,
          cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
        },
      ],
    },
  ];

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (
        [
          'maker',
          'fatMakerInspectorNm',
          'fatEnsolInspectorNm',
          'satMakerInspectorNm',
          'satEnsolInspectorNm',
        ].includes(binding)
      ) {
        cell.ariaReadOnly = false;
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        // Maker
        if ('maker' === binding) {
          setPartnerModalCondition({
            excpNm: item?.maker || '',
            selectedIds: item?.makerCd ? [item.makerCd] : undefined,
            target: {
              binding: binding,
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenPartnerModal(true);
        }
        // Maker 담당자
        else if (binding.endsWith('MakerInspectorNm')) {
          setPartnerUserModalCondition({
            userId: '',
            excpNm: item?.maker || '',
            excpId: item?.makerCd || '',
            selectedIds: item[binding.replace('Nm', '')]
              ? [item[binding.replace('Nm', '')]]
              : undefined,
            target: {
              binding: binding,
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenPartnerUserModal(true);
        }
        // 엔솔 담당자
        else if (binding.endsWith('EnsolInspectorNm')) {
          setUserModalCondition({
            userId: '',
            target: {
              binding: binding,
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenUserModal(true);
        }
      }
    });
  };

  useEffect(() => {
    props.callBackRowData(rowData);
  });

  return (
    <div>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ip.label.대상설비', '대상설비')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{t('ip.label.Maker', 'Maker')}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="maker"
                  className="find"
                  value={templateValue?.maker}
                  readOnly={true}
                  onSearchClick={() => {
                    setPartnerModalCondition({
                      excpNm: templateValue?.maker || '',
                      selectedIds: templateValue?.makerCd ? [templateValue.makerCd] : undefined,
                      target: {
                        binding: 'maker',
                      },
                    });
                    setOpenPartnerModal(true);
                  }}
                />
              </TableCell>
              <TableCell>
                <span>{t('ip.label.Maker담당자', 'Maker담당자')}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="makerInspector"
                  className="find"
                  value={templateValue?.makerInspectorNm}
                  readOnly={true}
                  onSearchClick={() => {
                    setPartnerUserModalCondition({
                      userId: '', // templateValue.makerInspector || ''
                      excpNm: templateValue?.maker || '',
                      excpId: templateValue?.makerCd || '',
                      selectedIds: templateValue?.makerInspector
                        ? [templateValue.makerInspector]
                        : undefined,
                      target: {
                        binding: 'makerInspectorNm',
                      },
                    });
                    setOpenPartnerUserModal(true);
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{t('ip.label.FAT 엔솔담당자', 'FAT 엔솔담당자')}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="fatEnsolInspector"
                  className="find"
                  value={templateValue?.fatEnsolInspectorNm}
                  readOnly={true}
                  onSearchClick={() => {
                    setUserModalCondition({
                      userId: '',
                      target: {
                        binding: 'fatEnsolInspectorNm',
                      },
                    });
                    setOpenUserModal(true);
                  }}
                />
              </TableCell>
              <TableCell>
                <span>{t('ip.label.SAT 엔솔담당자', 'SAT 엔솔담당자')}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="satEnsolInspector"
                  className="find"
                  value={templateValue?.satEnsolInspectorNm}
                  readOnly={true}
                  onSearchClick={() => {
                    setUserModalCondition({
                      userId: '',
                      target: {
                        binding: 'satEnsolInspectorNm',
                      },
                    });
                    setOpenUserModal(true);
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <SubTitleLayout>
        <SubTitleGroup>
          <div className="info warning">
            {t(
              'ip.label.대상설비형식',
              'FAT 엔솔담당자! Main설비 ID : A1ASTK201, 설비명 : STK설비 형식으로 작성'
            )}
          </div>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="plus" disableRipple onClick={btnAddRow}>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="minus" disableRipple onClick={handleDelRow}>
            {t('com.button.행삭제', '행삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={300}
        initialized={onInitialized}
      />

      {isOpenPartnerUserModal && (
        <PartnerUserModal
          open={isOpenPartnerUserModal}
          condition={{
            userId: partnerUserModalCondition?.userId || '',
            excpNm: partnerUserModalCondition?.excpNm || '',
            excpId: partnerUserModalCondition?.excpId || '',
          }}
          selectedIds={partnerUserModalCondition?.selectedIds}
          onCallback={(result) => {
            const target = partnerUserModalCondition.target;
            if (target && target?.binding) {
              const user = result as PartnerUser;
              if (target?.row > -1) {
                // grid 내 협력사 담당자 조회 콜백
                const item = gridRef.current?.rows[target.row].dataItem;

                // 협력업체가 변경된 경우 협력사담당자 정보 초기화
                if (item.makerCd !== user?.excpId) {
                  item.fatMakerInspector = '';
                  item.fatMakerInspectorNm = '';
                  item.satMakerInspector = '';
                  item.satMakerInspectorNm = '';
                  // 협력사 담당자 정보에 맞춰 협력사 정보 변경
                  item.maker = user?.excpNm || '';
                  item.makerCd = user?.excpId || '';
                }
                item[target.binding] = user?.userNm;
                item[target.binding.replace('Nm', '')] = user?.userId;
                item.crudKey = CrudCode.UPDATE;
                gridRef.current?.collectionView.refresh();
              } else {
                setTemplateValue((prev) => ({
                  ...prev,
                  [target.binding]: user?.userNm,
                  [target.binding.replace('Nm', '')]: user?.userId,
                  maker: user?.excpNm || '', // 협력사 담당자 정보에 맞춰 협력사 정보 변경
                  makerCd: user?.excpId || '',
                }));
              }
            }
          }}
          close={() => setOpenPartnerUserModal(false)}
        />
      )}

      {isOpenPartnerModal && (
        <PartnerModal
          open={isOpenPartnerModal}
          condition={{
            excpNm: partnerModalCondition?.excpNm || '',
          }}
          selectedIds={partnerModalCondition?.selectedIds}
          onCallback={(result) => {
            const target = partnerModalCondition.target;
            if (target && target?.binding) {
              const partner = result as Partner;
              if (target?.row > -1) {
                // grid 내 협력사 조회 콜백
                const item = gridRef.current?.rows[target.row].dataItem;
                // 협력업체가 변경된 경우 협력사담당자 정보 초기화
                if (item.makerCd !== partner?.excpId) {
                  item.fatMakerInspector = '';
                  item.fatMakerInspectorNm = '';
                  item.satMakerInspector = '';
                  item.satMakerInspectorNm = '';
                }
                item.maker = partner?.excpNm || '';
                item.makerCd = partner?.excpId || '';
                item.crudKey = CrudCode.UPDATE;
                gridRef.current?.collectionView.refresh();
              } else {
                setTemplateValue((prev) => ({
                  ...prev,
                  maker: partner?.excpNm || '',
                  makerCd: partner?.excpId || '',
                  // 협력업체가 변경된 경우 협력사담당자 정보 초기화
                  makerInspector: prev?.makerCd === partner?.excpId ? prev?.makerInspector : '',
                  makerInspectorNm: prev?.makerCd === partner?.excpId ? prev?.makerInspectorNm : '',
                }));
              }
            }
          }}
          close={() => setOpenPartnerModal(false)}
        />
      )}

      {isOpenUserModal && (
        <UserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.사용자 조회', '사용자 조회')}
          condition={{ userId: userModalCondition?.userId }}
          onCallback={(result) => {
            const target = userModalCondition.target;
            if (target && target?.binding) {
              const user = result as Employee;
              if (target?.row > -1) {
                // grid 내 엔솔 담당자 조회 콜백
                const item = gridRef.current?.rows[target.row].dataItem;

                item[target.binding] = user?.empNm;
                item[target.binding.replace('Nm', '')] = user?.userId;
                item.crudKey = CrudCode.UPDATE;
                gridRef.current?.collectionView.refresh();
              } else {
                setTemplateValue((prev) => ({
                  ...prev,
                  [target.binding]: user?.empNm,
                  [target.binding.replace('Nm', '')]: user?.userId,
                }));
              }
            }
          }}
        />
      )}
    </div>
  );
});

FacilityTargetGrid.displayName = '대상 설비';
export default memo(FacilityTargetGrid);
